import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Download, X, Maximize2, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-audit-tool/artifacts/aj4ztq87_image.png";

// Color helpers
const getBarColor = (score) => {
  if (score >= 85) return "#00FF87";
  if (score >= 70) return "#00D4FF";
  if (score >= 50) return "#FFD600";
  return "#FF3366";
};
const getStatusIcon = (answer) => {
  if (answer === "yes") return { color: "#00FF87", symbol: "\u2713", bg: "rgba(0,255,135,0.12)" };
  if (answer === "partial") return { color: "#FFD600", symbol: "!", bg: "rgba(255,214,0,0.12)" };
  if (answer === "no") return { color: "#FF3366", symbol: "\u2717", bg: "rgba(255,51,102,0.12)" };
  return { color: "#666", symbol: "\u2014", bg: "rgba(102,102,102,0.1)" };
};

const SECTION_DISPLAY = {
  discover: { label: "Desktop Version", subtitle: "Discover / Branding & Product Experience", icon: "\uD83D\uDDA5" },
  mobile: { label: "Mobile Version", subtitle: "Discover Mobile / Mobile Optimization", icon: "\uD83D\uDCF1" },
  drive: { label: "Marketing Readiness", subtitle: "Drive / Tracking & Analytics", icon: "\uD83D\uDCE3" },
  dominate: { label: "SEO Readiness", subtitle: "Dominate / Search Visibility", icon: "\uD83D\uDD0D" },
  discourse: { label: "Social Media", subtitle: "Discourse / Social Footprint", icon: "\uD83D\uDCAC" },
  deliver: { label: "Packaging Experience", subtitle: "Deliver / Post-Purchase & Packaging", icon: "\uD83D\uDCE6" },
  delight: { label: "Customer Support", subtitle: "Delight / Support Experience", icon: "\u2764\uFE0F" },
};

// Animated number counter
function AnimatedNumber({ value, duration = 1500, suffix = "%" }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let start = 0;
    const end = Math.round(value);
    if (end === 0) { setDisplay(0); return; }
    const step = Math.max(1, Math.floor(end / (duration / 30)));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setDisplay(end); clearInterval(timer); }
      else setDisplay(start);
    }, 30);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span ref={ref}>{display}{suffix}</span>;
}

// Animated bar
function AnimBar({ score, color, delay = 0 }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(score), 200 + delay);
    return () => clearTimeout(t);
  }, [score, delay]);
  return (
    <div className="h-8 rounded-full bg-zinc-800/60 overflow-hidden flex-1">
      <div className="h-full rounded-full transition-all ease-out" style={{ width: `${width}%`, backgroundColor: color, transitionDuration: "1.2s", transitionDelay: `${delay}ms` }} />
    </div>
  );
}

// Donut chart SVG
function DonutChart({ score, size = 160, strokeWidth = 14, color }) {
  const [offset, setOffset] = useState(100);
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  useEffect(() => {
    const t = setTimeout(() => setOffset(100 - score), 300);
    return () => clearTimeout(t);
  }, [score]);
  const c = color || getBarColor(score);
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#27272A" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth={strokeWidth}
        strokeDasharray={circ} strokeDashoffset={(offset / 100) * circ}
        strokeLinecap="round" style={{ transition: "stroke-dashoffset 1.5s ease-out" }} />
    </svg>
  );
}

// Slide wrapper with fade animation
function Slide({ active, children }) {
  return (
    <div className={cn(
      "absolute inset-0 flex flex-col transition-all duration-500",
      active ? "opacity-100 translate-x-0 z-10" : "opacity-0 translate-x-8 z-0 pointer-events-none"
    )}>
      {children}
    </div>
  );
}

// ============ SLIDE COMPONENTS ============

function CoverSlide({ audit, active }) {
  const ci = audit.client_info || {};
  return (
    <Slide active={active}>
      <div className="flex-1 flex flex-col items-center justify-center px-12 relative">
        {/* Header logos */}
        <div className="absolute top-6 left-8 right-8 flex justify-between items-start">
          <div className="text-2xl font-bold text-white/80">{ci.store_name}</div>
          <img src={LOGO_URL} alt="DI" className="h-10 opacity-80" />
        </div>
        {/* Center content */}
        <div className="inline-block px-6 py-2 rounded-full border border-emerald-500/40 text-emerald-400 text-sm tracking-widest mb-6"
          style={{ animationDelay: "0.3s" }}>DIGITAL IMPRESSIONS</div>
        <h1 className="text-6xl font-black text-white tracking-tight mb-2">IMPRESS 360</h1>
        <h2 className="text-5xl font-black bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-6">Audit Report</h2>
        <p className="text-zinc-400 text-lg">{ci.store_name} | {ci.audit_date ? new Date(ci.audit_date).toLocaleDateString('en-US', {month: 'long', year: 'numeric'}) : ''}</p>
        <div className="mt-8 px-6 py-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 text-zinc-300 text-sm italic">
          "From audit insights to smarter growth decisions."
        </div>
        {/* Decorative elements */}
        <div className="absolute left-0 top-16 w-1 h-16 bg-gradient-to-b from-emerald-400 to-transparent rounded-full" />
        <div className="absolute right-0 top-1/3 w-1 h-32 bg-gradient-to-b from-emerald-400 to-transparent rounded-full" />
      </div>
    </Slide>
  );
}

function ExecutiveSlide({ audit, insights, active }) {
  const totalIssues = Object.values(audit.sections || {}).reduce((sum, s) => {
    return sum + (s.answers || []).filter(a => a.answer === "no" || a.answer === "partial").length;
  }, 0);
  const completion = audit.total_questions > 0 ? Math.round((audit.answered_questions / audit.total_questions) * 100) : 0;
  return (
    <Slide active={active}>
      <div className="flex-1 px-12 pt-8 flex flex-col">
        <div className="flex justify-between items-start mb-2">
          <div><div className="text-zinc-500 text-xs mb-1">{audit.client_info?.store_name}</div></div>
          <img src={LOGO_URL} alt="DI" className="h-8 opacity-70" />
        </div>
        <h2 className="text-4xl font-black text-white mb-1">Executive Snapshot</h2>
        <div className="w-16 h-1 bg-emerald-400 rounded-full mb-6" />
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-6 flex flex-col items-start">
            <span className="text-xs text-zinc-400 tracking-wider mb-2">OVERALL SCORE</span>
            <span className="text-5xl font-black" style={{ color: getBarColor(audit.overall_score) }}>
              <AnimatedNumber value={audit.overall_score} />
            </span>
            <div className="mt-3 self-center"><DonutChart score={audit.overall_score} size={100} strokeWidth={10} /></div>
          </div>
          <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-6">
            <span className="text-xs text-zinc-400 tracking-wider mb-2 block">ISSUES IDENTIFIED</span>
            <span className="text-5xl font-black text-yellow-400"><AnimatedNumber value={totalIssues} suffix="" /></span>
            <p className="text-zinc-500 text-sm mt-2">Requires attention</p>
          </div>
          <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-6">
            <span className="text-xs text-zinc-400 tracking-wider mb-2 block">QUESTIONS COVERED</span>
            <span className="text-5xl font-black text-cyan-400"><AnimatedNumber value={audit.answered_questions || 0} suffix="" /></span>
            <p className="text-zinc-500 text-sm mt-2">{completion}% completion</p>
          </div>
        </div>
        <div className="rounded-2xl bg-zinc-800/40 border border-zinc-700/40 p-5 mt-auto mb-4">
          <span className="text-xs text-emerald-400 tracking-wider font-bold">KEY INSIGHT</span>
          <p className="text-zinc-300 mt-2 text-sm leading-relaxed">{insights?.executive_summary || "Audit analysis complete."}</p>
        </div>
      </div>
    </Slide>
  );
}

function SectionComparisonSlide({ audit, active }) {
  const sectionOrder = ["discover", "mobile", "drive", "dominate", "discourse", "deliver", "delight"];
  const sorted = sectionOrder
    .filter(id => audit.sections?.[id])
    .map(id => ({ id, ...audit.sections[id] }))
    .sort((a, b) => a.score - b.score);
  return (
    <Slide active={active}>
      <div className="flex-1 px-12 pt-8 flex flex-col">
        <h2 className="text-4xl font-black text-white mb-1">Section Score Comparison</h2>
        <div className="w-16 h-1 bg-emerald-400 rounded-full mb-2" />
        <p className="text-zinc-500 text-sm mb-6">Performance across all audit categories</p>
        <div className="flex-1 flex flex-col justify-center gap-4">
          {sorted.map((s, i) => {
            const display = SECTION_DISPLAY[s.id] || {};
            const color = getBarColor(s.score);
            return (
              <div key={s.id} className="flex items-center gap-4">
                <span className="text-zinc-300 text-sm w-44 text-right shrink-0">{display.label || s.section_name}</span>
                <AnimBar score={s.score} color={color} delay={i * 120} />
                <span className="text-white font-bold text-sm w-12">{Math.round(s.score)}%</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-6 justify-center mt-4 mb-4 text-xs text-zinc-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{background:"#FF3366"}} /> High Priority</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{background:"#FFD600"}} /> Medium Priority</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{background:"#00D4FF"}} /> Good</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{background:"#00FF87"}} /> Excellent</span>
        </div>
      </div>
    </Slide>
  );
}

function HealthOverviewSlide({ audit, active }) {
  let totalYes = 0, totalNo = 0, totalPartial = 0, totalNa = 0;
  Object.values(audit.sections || {}).forEach(s => {
    (s.answers || []).forEach(a => {
      if (a.answer === "yes") totalYes++;
      else if (a.answer === "no") totalNo++;
      else if (a.answer === "partial") totalPartial++;
      else totalNa++;
    });
  });
  const total = totalYes + totalNo + totalPartial + totalNa;
  const sections = Object.entries(audit.sections || {}).map(([id, s]) => ({ id, ...s })).sort((a, b) => b.score - a.score);
  const strongest = sections.slice(0, 2);
  const weakest = sections.slice(-2).reverse();
  return (
    <Slide active={active}>
      <div className="flex-1 px-12 pt-8 flex flex-col">
        <h2 className="text-4xl font-black text-white mb-1">Audit Health Overview</h2>
        <div className="w-16 h-1 bg-emerald-400 rounded-full mb-6" />
        <div className="grid grid-cols-2 gap-6 flex-1">
          <div className="flex flex-col gap-4">
            {/* Donut + stats */}
            <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-5 flex items-center gap-6">
              <div className="relative">
                <DonutChart score={total > 0 ? (totalYes / total) * 100 : 0} size={120} strokeWidth={12} color="#00FF87" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{total > 0 ? Math.round((totalYes / total) * 100) : 0}%</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 flex-1">
                <div className="text-center"><span className="text-2xl font-bold text-emerald-400">{totalYes}</span><p className="text-xs text-zinc-500">Passed</p></div>
                <div className="text-center"><span className="text-2xl font-bold text-yellow-400">{totalPartial}</span><p className="text-xs text-zinc-500">Partial</p></div>
                <div className="text-center"><span className="text-2xl font-bold text-pink-400">{totalNo}</span><p className="text-xs text-zinc-500">Failed</p></div>
                <div className="text-center"><span className="text-2xl font-bold text-zinc-500">{totalNa}</span><p className="text-xs text-zinc-500">N/A</p></div>
              </div>
            </div>
            {/* Stacked bar */}
            <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-5">
              <p className="text-xs text-zinc-400 mb-3">Response Distribution</p>
              <div className="h-6 rounded-full overflow-hidden flex">
                {total > 0 && <>
                  <div style={{ width: `${(totalYes/total)*100}%`, background: "#00FF87" }} className="transition-all duration-1000" />
                  <div style={{ width: `${(totalPartial/total)*100}%`, background: "#FFD600" }} className="transition-all duration-1000" />
                  <div style={{ width: `${(totalNo/total)*100}%`, background: "#FF3366" }} className="transition-all duration-1000" />
                  <div style={{ width: `${(totalNa/total)*100}%`, background: "#444" }} className="transition-all duration-1000" />
                </>}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-5">
              <p className="text-xs text-emerald-400 tracking-wider font-bold mb-3">STRONGEST SECTIONS</p>
              {strongest.map(s => (
                <div key={s.id} className="flex justify-between items-center py-2 border-b border-zinc-800 last:border-0">
                  <span className="text-zinc-300 text-sm">{SECTION_DISPLAY[s.id]?.label || s.section_name}</span>
                  <span className="text-emerald-400 font-bold">{Math.round(s.score)}%</span>
                </div>
              ))}
            </div>
            <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-5">
              <p className="text-xs text-pink-400 tracking-wider font-bold mb-3">WEAKEST SECTIONS</p>
              {weakest.map(s => (
                <div key={s.id} className="flex justify-between items-center py-2 border-b border-zinc-800 last:border-0">
                  <span className="text-zinc-300 text-sm">{SECTION_DISPLAY[s.id]?.label || s.section_name}</span>
                  <span className="text-pink-400 font-bold">{Math.round(s.score)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

function HeatmapSlide({ audit, insights, active }) {
  const sections = Object.entries(audit.sections || {}).map(([id, s]) => ({ id, ...s }));
  const highConcern = sections.filter(s => s.score < 70).sort((a,b) => a.score - b.score);
  const needsImprovement = sections.filter(s => s.score >= 70 && s.score < 85).sort((a,b) => a.score - b.score);
  const strong = sections.filter(s => s.score >= 85).sort((a,b) => b.score - a.score);
  const sInsights = insights?.section_insights || {};
  const renderCard = (s, colorClass, borderColor) => (
    <div key={s.id} className={`rounded-xl p-4 border ${borderColor} bg-zinc-800/40`}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-white text-sm font-semibold">{SECTION_DISPLAY[s.id]?.label || s.section_name}</span>
        <span className={`font-bold text-lg ${colorClass}`}>{Math.round(s.score)}%</span>
      </div>
      <p className="text-zinc-500 text-xs">{sInsights[s.id]?.insight || ""}</p>
    </div>
  );
  return (
    <Slide active={active}>
      <div className="flex-1 px-12 pt-8 flex flex-col">
        <h2 className="text-4xl font-black text-white mb-1">Priority Heatmap</h2>
        <div className="w-16 h-1 bg-emerald-400 rounded-full mb-6" />
        <div className="grid grid-cols-3 gap-6 flex-1">
          <div>
            <div className="flex items-center gap-2 mb-4"><span className="w-3 h-3 rounded-full bg-pink-500" /><span className="text-pink-400 text-sm font-bold">High Concern (&lt;70%)</span></div>
            <div className="flex flex-col gap-3">
              {highConcern.length ? highConcern.map(s => renderCard(s, "text-pink-400", "border-pink-500/30")) : <p className="text-zinc-600 text-sm">None</p>}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-4"><span className="w-3 h-3 rounded-full bg-yellow-500" /><span className="text-yellow-400 text-sm font-bold">Needs Improvement (70-85%)</span></div>
            <div className="flex flex-col gap-3">
              {needsImprovement.length ? needsImprovement.map(s => renderCard(s, "text-yellow-400", "border-yellow-500/30")) : <p className="text-zinc-600 text-sm">None</p>}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-4"><span className="w-3 h-3 rounded-full bg-emerald-500" /><span className="text-emerald-400 text-sm font-bold">Strong / Maintain (85%+)</span></div>
            <div className="flex flex-col gap-3">
              {strong.length ? strong.map(s => renderCard(s, "text-emerald-400", "border-emerald-500/30")) : <p className="text-zinc-600 text-sm">None</p>}
            </div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

function SectionDetailSlide({ audit, sectionId, questionDefs, insights, active }) {
  const sData = audit.sections?.[sectionId] || {};
  const display = SECTION_DISPLAY[sectionId] || {};
  const answers = sData.answers || [];
  const qMap = {};
  (questionDefs || []).forEach(q => { qMap[q.id] = q; });
  
  // Pick top 5-7 important questions: failed first, then partial, then high-weight passed
  const scored = answers.map(a => {
    const qDef = qMap[a.question_id] || {};
    let priority = 0;
    if (a.answer === "no") priority = 100 + (qDef.weight || 1) * 10;
    else if (a.answer === "partial") priority = 50 + (qDef.weight || 1) * 10;
    else if (a.answer === "yes") priority = (qDef.weight || 1);
    return { ...a, text: qDef.text || a.question_id, weight: qDef.weight || 1, priority };
  }).sort((a, b) => b.priority - a.priority).slice(0, 7);
  
  const yesCount = answers.filter(a => a.answer === "yes").length;
  const noCount = answers.filter(a => a.answer === "no").length;
  const partialCount = answers.filter(a => a.answer === "partial").length;
  const sInsight = insights?.section_insights?.[sectionId]?.insight || "";
  
  return (
    <Slide active={active}>
      <div className="flex-1 px-12 pt-8 flex flex-col">
        <div className="flex justify-between items-start mb-2">
          <div className="text-zinc-500 text-xs">{audit.client_info?.store_name}</div>
          <img src={LOGO_URL} alt="DI" className="h-7 opacity-70" />
        </div>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-3xl font-black text-white flex items-center gap-3">
              <span className="text-2xl">{display.icon}</span> {display.label}
            </h2>
            <p className="text-zinc-500 text-sm">{display.subtitle}</p>
          </div>
          <div className="text-right">
            <span className="text-5xl font-black" style={{ color: getBarColor(sData.score || 0) }}>
              {Math.round(sData.score || 0)}%
            </span>
            <p className="text-zinc-500 text-xs">Overall Score</p>
          </div>
        </div>
        <div className="grid grid-cols-5 gap-4 flex-1">
          {/* Left: Question checklist (3 cols) */}
          <div className="col-span-3 flex flex-col gap-2 overflow-hidden">
            {scored.map((q, i) => {
              const st = getStatusIcon(q.answer);
              return (
                <div key={q.question_id} className="flex items-center gap-3 rounded-xl bg-zinc-800/40 border border-zinc-700/30 px-4 py-2.5"
                  style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                    style={{ background: st.bg, color: st.color, border: `2px solid ${st.color}` }}>
                    {st.symbol}
                  </div>
                  <span className="text-zinc-300 text-xs leading-snug">{q.text}</span>
                </div>
              );
            })}
          </div>
          {/* Right: Mini charts + insight (2 cols) */}
          <div className="col-span-2 flex flex-col gap-3">
            <div className="rounded-xl bg-zinc-800/40 border border-zinc-700/30 p-4">
              <p className="text-xs text-cyan-400 font-bold mb-3">Status Summary</p>
              {[
                { label: "Passed", count: yesCount, color: "#00FF87" },
                { label: "Partial", count: partialCount, color: "#FFD600" },
                { label: "Failed", count: noCount, color: "#FF3366" },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: item.color }} />
                  <span className="text-zinc-400 text-xs flex-1">{item.label}</span>
                  <div className="w-24 h-2 rounded-full bg-zinc-700 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${answers.length ? (item.count / answers.length) * 100 : 0}%`, background: item.color, transition: "width 1s ease-out" }} />
                  </div>
                  <span className="text-zinc-400 text-xs w-6 text-right">{item.count}</span>
                </div>
              ))}
            </div>
            {sInsight && (
              <div className="rounded-xl bg-zinc-800/40 border border-zinc-700/30 p-4 mt-auto">
                <p className="text-xs text-emerald-400 font-bold mb-2">KEY MESSAGE</p>
                <p className="text-zinc-400 text-xs leading-relaxed">{sInsight}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Slide>
  );
}

function FinalSummarySlide({ audit, insights, active }) {
  const sections = Object.entries(audit.sections || {}).map(([id, s]) => ({ id, ...s })).sort((a, b) => b.score - a.score);
  const top3 = sections.slice(0, 3);
  const bottom3 = sections.slice(-3).reverse();
  const totalIssues = Object.values(audit.sections || {}).reduce((sum, s) => {
    return sum + (s.answers || []).filter(a => a.answer === "no").length;
  }, 0);
  return (
    <Slide active={active}>
      <div className="flex-1 px-12 pt-8 flex flex-col">
        <h2 className="text-4xl font-black text-white mb-1">Final Audit Summary</h2>
        <div className="w-16 h-1 bg-emerald-400 rounded-full mb-6" />
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-6 text-center">
            <span className="text-xs text-zinc-400 tracking-wider">OVERALL SCORE</span>
            <div className="text-6xl font-black mt-2" style={{ color: getBarColor(audit.overall_score) }}>
              <AnimatedNumber value={audit.overall_score} />
            </div>
          </div>
          <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-5">
            <span className="text-xs text-emerald-400 tracking-wider font-bold">TOP 3 STRENGTHS</span>
            {top3.map(s => (
              <div key={s.id} className="flex justify-between py-1.5 border-b border-zinc-800 last:border-0">
                <span className="text-zinc-300 text-sm">{SECTION_DISPLAY[s.id]?.label || s.section_name}</span>
                <span className="text-emerald-400 font-bold text-sm">{Math.round(s.score)}%</span>
              </div>
            ))}
          </div>
          <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-5">
            <span className="text-xs text-pink-400 tracking-wider font-bold">TOP 3 GAPS</span>
            {bottom3.map(s => (
              <div key={s.id} className="flex justify-between py-1.5 border-b border-zinc-800 last:border-0">
                <span className="text-zinc-300 text-sm">{SECTION_DISPLAY[s.id]?.label || s.section_name}</span>
                <span className="text-pink-400 font-bold text-sm">{Math.round(s.score)}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl bg-zinc-800/40 border border-zinc-700/40 p-6 text-center mb-4">
          <span className="text-yellow-400 text-3xl font-black">{totalIssues}</span>
          <span className="text-zinc-400 ml-2">total issues identified across all sections</span>
        </div>
        <div className="rounded-2xl bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 p-6 mt-auto mb-4">
          <p className="text-zinc-200 text-sm leading-relaxed text-center italic">
            "{insights?.closing_statement || "Audit analysis complete. Review section details for specific recommendations."}"
          </p>
        </div>
        <p className="text-center text-zinc-600 text-xs">Prepared by Digital Impressions | IMPRESS 360 Audit</p>
      </div>
    </Slide>
  );
}

// ============ MAIN PRESENTATION COMPONENT ============

export default function Presentation() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareLink, setShareLink] = useState("");
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef(null);
  
  const sectionOrder = ["discover", "mobile", "drive", "dominate", "discourse", "deliver", "delight"];
  const totalSlides = 5 + sectionOrder.filter(id => data?.audit?.sections?.[id]).length + 1;
  
  const goNext = useCallback(() => setCurrentSlide(prev => Math.min(prev + 1, totalSlides - 1)), [totalSlides]);
  const goPrev = useCallback(() => setCurrentSlide(prev => Math.max(prev - 1, 0)), []);
  
  useEffect(() => {
    const fetchPresentation = async () => {
      try {
        const resp = await axios.get(`${API}/audits/${auditId}/presentation`);
        setData(resp.data);
      } catch (err) {
        console.error("Presentation fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPresentation();
  }, [auditId]);
  
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); goNext(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
      if (e.key === "Escape" && document.fullscreenElement) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev]);
  
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const handleShare = async () => {
    setSharing(true);
    try {
      const resp = await axios.post(`${API}/audits/${auditId}/presentation/share`, { email: shareEmail || undefined });
      setShareLink(resp.data.share_url);
      if (shareEmail) toast.success(`Share link sent to ${shareEmail}`);
      else toast.success("Public share link generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate share link");
    } finally {
      setSharing(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPDF = () => {
    // Inject print styles for dark background
    const style = document.createElement("style");
    style.id = "print-dark-bg";
    style.textContent = `
      @media print {
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
        html, body { background: #000 !important; }
        .presentation-slide-area { background: #09090b !important; box-shadow: none !important; border: none !important; }
        .no-print { display: none !important; }
        @page { size: landscape; margin: 0; }
      }
    `;
    document.head.appendChild(style);
    window.print();
    setTimeout(() => style.remove(), 1000);
  };
  
  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-emerald-400 text-lg">Loading presentation...</div>
    </div>
  );
  
  if (!data?.audit) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-red-400">Audit not found</div>
    </div>
  );
  
  const { audit, section_definitions, presentation } = data;
  const insights = presentation?.ai_insights || {};
  const activeSections = sectionOrder.filter(id => audit.sections?.[id]);
  
  // Build slides array
  let slideIndex = 0;
  const slides = [];
  slides.push(<CoverSlide key="cover" audit={audit} active={currentSlide === slideIndex++} />);
  slides.push(<ExecutiveSlide key="exec" audit={audit} insights={insights} active={currentSlide === slideIndex++} />);
  slides.push(<SectionComparisonSlide key="compare" audit={audit} active={currentSlide === slideIndex++} />);
  slides.push(<HealthOverviewSlide key="health" audit={audit} active={currentSlide === slideIndex++} />);
  slides.push(<HeatmapSlide key="heatmap" audit={audit} insights={insights} active={currentSlide === slideIndex++} />);
  activeSections.forEach(sid => {
    slides.push(
      <SectionDetailSlide key={sid} audit={audit} sectionId={sid} questionDefs={section_definitions?.[sid]?.questions}
        insights={insights} active={currentSlide === slideIndex++} />
    );
  });
  slides.push(<FinalSummarySlide key="final" audit={audit} insights={insights} active={currentSlide === slideIndex++} />);
  
  const actualTotalSlides = slides.length;
  
  return (
    <div ref={containerRef} className="min-h-screen bg-black flex flex-col" data-testid="presentation-view">
      {/* Presentation area - 16:9 */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="relative w-full bg-zinc-950 rounded-xl overflow-hidden shadow-2xl border border-zinc-800/50 presentation-slide-area"
          style={{ maxWidth: "1280px", aspectRatio: "16/9" }}>
          {/* Decorative side accents */}
          <div className="absolute left-0 top-12 w-0.5 h-12 bg-gradient-to-b from-emerald-400 to-transparent rounded-full z-20" />
          <div className="absolute right-0 top-1/4 w-0.5 h-24 bg-gradient-to-b from-emerald-400 to-transparent rounded-full z-20" />
          
          {/* Slides container */}
          <div className="absolute inset-0">
            {slides}
          </div>
          
          {/* Navigation arrows */}
          {currentSlide > 0 && (
            <button onClick={goPrev} className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center text-white transition-colors"
              data-testid="prev-slide-btn">
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {currentSlide < actualTotalSlides - 1 && (
            <button onClick={goNext} className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center text-white transition-colors"
              data-testid="next-slide-btn">
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          
          {/* Bottom: dot indicators */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5">
            {Array.from({ length: actualTotalSlides }).map((_, i) => (
              <button key={i} onClick={() => setCurrentSlide(i)}
                className={cn("h-2 rounded-full transition-all", i === currentSlide ? "w-6 bg-emerald-400" : "w-2 bg-zinc-600 hover:bg-zinc-500")} />
            ))}
          </div>
          <div className="absolute bottom-3 right-4 z-30 text-zinc-500 text-xs">{currentSlide + 1} / {actualTotalSlides}</div>
        </div>
      </div>
      
      {/* Controls bar */}
      <div className="flex items-center justify-center gap-3 pb-4 no-print">
        <button onClick={() => navigate(`/dashboard/${auditId}`)}
          className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm flex items-center gap-2 transition-colors"
          data-testid="back-to-dashboard-btn">
          <X className="w-4 h-4" /> Back
        </button>
        <button onClick={toggleFullscreen}
          className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm flex items-center gap-2 transition-colors"
          data-testid="fullscreen-btn">
          <Maximize2 className="w-4 h-4" /> {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        </button>
        <button onClick={() => setShowShareModal(true)}
          className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm flex items-center gap-2 transition-colors"
          data-testid="share-btn">
          <Share2 className="w-4 h-4" /> Share
        </button>
        <button onClick={handleDownloadPDF}
          className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm flex items-center gap-2 transition-colors"
          data-testid="download-pdf-btn">
          <Download className="w-4 h-4" /> Download PDF
        </button>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center no-print" onClick={() => setShowShareModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-white text-lg font-bold mb-4">Share Presentation</h3>
            <p className="text-zinc-400 text-sm mb-4">Generate a public view-only link for this presentation. Optionally enter client email.</p>
            <input
              type="email"
              placeholder="Client email (optional)"
              value={shareEmail}
              onChange={e => setShareEmail(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-zinc-800 border border-zinc-700 text-white text-sm placeholder:text-zinc-500 mb-3 outline-none focus:border-emerald-500"
              data-testid="share-email-input"
            />
            <button onClick={handleShare} disabled={sharing}
              className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 mb-3"
              data-testid="generate-share-link-btn">
              {sharing ? "Generating..." : "Generate Share Link"}
            </button>
            {shareLink && (
              <div className="flex items-center gap-2 bg-zinc-800 rounded-xl p-3 border border-zinc-700">
                <input type="text" value={shareLink} readOnly className="flex-1 bg-transparent text-emerald-400 text-xs outline-none" />
                <button onClick={copyLink} className="shrink-0 text-zinc-400 hover:text-white">
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            )}
            <button onClick={() => setShowShareModal(false)} className="w-full mt-3 py-2 text-zinc-500 text-sm hover:text-zinc-300">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
